import { t } from '@lingui/macro';
import { ColDef } from 'ag-grid-community';
import { useApplicationTableState } from 'contexts/ApplicationTableStateContext/ApplicationTableStateContext';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { compareAsc, parseISO } from 'date-fns';
import { useLocale } from 'hooks/useLocale';
import { useNumberFormat } from 'hooks/useNumberFormat';
import { APPLICATION_TABLE_KEYS } from 'utils/applications/applications.config';
import {
  deriveProductFromTypeAndDuration,
  getDurations,
  getProductNames,
  isCrossSellProduct,
} from './applicationUtils';

export const ColumnDefinitions = ({
  showReferredByColumn,
  hasPrimeLoans,
}: {
  hasPrimeLoans?: boolean;
  showReferredByColumn: boolean;
}): ColDef<any>[] => {
  const { isDE, isUK } = useLocale();
  const { formatCurrency } = useNumberFormat();
  const { columnConfig } = useApplicationTableState();
  const { introducer } = useIntroducerContext();

  const getCreatedColumnWidth = () => {
    if (introducer?.can_see_partner_applications) {
      return 160;
    }

    if (isDE) {
      return 140;
    }

    return 118;
  };

  const columns: { [key: string]: ColDef<any> | null } = {
    [APPLICATION_TABLE_KEYS.created]: {
      headerName: t`Created`,
      field: 'created',
      cellRenderer: 'CreatedCell',
      valueGetter: (params) => dateValueExtractor(params.data.created),
      pinned: 'left',
      filter: 'agDateColumnFilter',
      filterParams: { buttons: ['reset'] },
      comparator: sortDateByAscending,
      minWidth: getCreatedColumnWidth(),
    },
    [APPLICATION_TABLE_KEYS.company_name]: {
      headerName: t`Company name`,
      field: 'company_name',
      valueGetter: 'data.company',
      cellRenderer: 'CompanyCell',
      pinned: 'left',
      resizable: false,
      suppressSizeToFit: true,
      minWidth: 272,
      cellClass: 'fs-exclude',
      filter: true,
      autoHeight: true,
      wrapText: true,
      cellStyle: { wordBreak: 'break-word' },
    },
    [APPLICATION_TABLE_KEYS.customer_name]: {
      headerName: t`Applicant`,
      cellClass: 'regText',
      field: 'customer_name',
      valueGetter: (params) => {
        const customer = params.data.customer;
        if (!customer.first_name && !customer.last_name) {
          return null;
        }
        return `${customer.first_name} ${customer.last_name}`;
      },
      minWidth: 182,
      filter: true,
    },
    [APPLICATION_TABLE_KEYS.status]: {
      headerName: t`Application status`,
      field: 'status',
      cellRenderer: 'ApplicationStatusCell',
      filter: 'TableSelectValuesFilter',
      minWidth: 232,
      filterParams: { filterName: 'status' },
    },
    [APPLICATION_TABLE_KEYS.term_length]: {
      headerName: t`Term length`,
      field: 'term_length',
      valueGetter: ({ data }) => getDurations(data),
      cellRenderer: 'TermLengthCell',
      filter: 'TableSelectValuesFilter',
      filterParams: { filterName: 'term_length', hasPrimeLoans },
      minWidth: 142,
    },
    [APPLICATION_TABLE_KEYS.product_type]: {
      headerName: t`Product`,
      field: 'product_type',
      valueGetter: ({ data }) => getProductNames(data, isUK),
      cellRenderer: 'ProductTypeCell',
      cellRendererParams: ({ data }: any) => {
        const duration = data?.requested_products[0]?.duration;
        const formalOfferProduct =
          data?.formal_offer_sets[0]?.formal_offers[0].product_type;
        const productType = data?.requested_products?.[0]?.product_type;
        const { type: derivedProductType } = deriveProductFromTypeAndDuration({
          productType,
          duration,
          isUK,
        });
        const crossSellProduct = isCrossSellProduct(
          formalOfferProduct,
          derivedProductType,
        );
        return { crossSellProduct };
      },
      filter: 'TableSelectValuesFilter',
      filterParams: { filterName: 'product_type', hasPrimeLoans },
      minWidth: 130,
    },
    [APPLICATION_TABLE_KEYS.approval_amount]: {
      headerName: t`Approval amount`,
      field: 'approval_amount',
      cellRenderer: 'ApprovalAmountCell',
      valueGetter: (params: any) =>
        params.data.formal_offer_sets?.[0]?.formal_offers?.[0]?.max_amount,
      filter: 'agNumberColumnFilter',
      comparator: sortNumberByAscending,
      minWidth: isDE ? 140 : 132,
    },
    [APPLICATION_TABLE_KEYS.requested_amount]: {
      headerName: t`Requested amount`,
      field: 'requested_amount',
      valueGetter: (params: any) => params.data.requested_products?.[0]?.amount,
      valueFormatter: (params: any) =>
        params.value && formatCurrency(params.value),
      filter: 'agNumberColumnFilter',
      comparator: sortNumberByAscending,
      minWidth: isDE ? 150 : 132,
    },
    [APPLICATION_TABLE_KEYS.expiry]: {
      headerName: t`Expiry date`,
      field: 'expiry',
      cellRenderer: 'OfferExpiryDateCell',
      minWidth: 122,
      valueGetter: (params: any) =>
        dateValueExtractor(
          params.data.formal_offer_sets?.[0]?.formal_offers?.[0]?.timeout,
        ),
      filter: 'agDateColumnFilter',
      filterParams: { buttons: ['reset'] },
      comparator: sortDateByAscending,
    },
    [APPLICATION_TABLE_KEYS.referred_by]: showReferredByColumn
      ? {
          headerName: t`Referred by`,
          field: 'referred_by',
          valueGetter: (params) => {
            const introducer = params.data.introducer;
            if (!introducer.first_name && !introducer.last_name) {
              return null;
            }
            return `${introducer.first_name} ${introducer.last_name}`;
          },
          minWidth: 105,
          filter: true,
          autoHeight: true,
          wrapText: true,
          cellStyle: { wordBreak: 'break-word' },
        }
      : null,
  };

  return [
    columns[APPLICATION_TABLE_KEYS.created],
    columns[APPLICATION_TABLE_KEYS.company_name],
    columns[APPLICATION_TABLE_KEYS.customer_name],
    columns[APPLICATION_TABLE_KEYS.status],
    ...columnConfig.map((item) => ({
      ...columns[item.key],
      hide: !item.display,
    })),
  ].filter((col) => !!col) as ColDef<any>[];
};

const sortNumberByAscending = (valueA: number, valueB: number) =>
  valueA - valueB;

export const dateValueExtractor = (ISODateString: string) => {
  if (!ISODateString) {
    return ISODateString;
  }

  const dateTime = parseISO(ISODateString);
  return new Date(
    dateTime.getFullYear(),
    dateTime.getMonth(),
    dateTime.getDate(),
  );
};

const sortDateByAscending = (dateA: Date, dateB: Date) => {
  let nonNullDateA = dateA;
  let nonNullDateB = dateB;

  // Check if the first date is null or undefined. If it is, set it to the earliest
  // representable date (-8640000000000000 represents the earliest timestamp).
  if (!dateA) {
    nonNullDateA = new Date(-8640000000000000);
  }

  if (!dateB) {
    nonNullDateB = new Date(-8640000000000000);
  }

  return compareAsc(nonNullDateA, nonNullDateB);
};
